import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { useCompanyName } from '../../contexts/SettingsContext';
import { fetchServiceMenuItems, NavigationMenuItem } from '../../services/navigationService';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useUserAuth();
  const companyName = useCompanyName();
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [serviceMenuItems, setServiceMenuItems] = useState<NavigationMenuItem[]>([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(true);

  // Focus management for accessibility
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);
  const servicesMenuRef = React.useRef<HTMLDivElement>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);

  // Fetch dynamic service menu items
  useEffect(() => {
    const loadServiceMenuItems = async () => {
      try {
        setIsLoadingMenu(true);
        const items = await fetchServiceMenuItems(i18n.language);
        setServiceMenuItems(items);
      } catch (error) {        // Fallback to empty array on error
        setServiceMenuItems([]);
      } finally {
        setIsLoadingMenu(false);
      }
    };

    loadServiceMenuItems();
  }, [i18n.language]);

  // Handle escape key press
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
        if (isServicesOpen) {
          setIsServicesOpen(false);
        }
        if (isUserMenuOpen) {
          setIsUserMenuOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isMobileMenuOpen, isServicesOpen, isUserMenuOpen]);
  
  const toggleLanguage = useCallback(() => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  }, [i18n]);

  const handleLogout = useCallback(() => {
    logout();
    setIsUserMenuOpen(false);
  }, [logout]);

  // Memoized animation variants for better performance
  const animationVariants = useMemo(() => ({
    navbar: {
      initial: { y: -100 },
      animate: { y: 0 },
      transition: { duration: 0.6, ease: "easeOut" as const }
    },
    menuItem: {
      initial: { opacity: 0, x: -10 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.2 }
    },
    dropdown: {
      initial: { opacity: 0, y: -10 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -10 },
      transition: { duration: 0.2 }
    }
  }), []);

  return (
    <motion.nav
      className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95"
      {...animationVariants.navbar}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link
              to="/"
              className="text-xl font-bold text-primary-600 hover:text-primary-700 transition-colors duration-200 flex items-center"
            >
              <motion.div
                className="flex items-center rtl:flex-row-reverse"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg flex items-center justify-center mr-2 rtl:ml-2">
                  <span className="text-white font-bold text-sm">P</span>
                </div>
                <span>{companyName}</span>
              </motion.div>
            </Link>
          </motion.div>

          <motion.div 
            className="hidden md:flex items-center space-x-8 rtl:space-x-reverse"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Home */}
            <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
              <Link 
                to="/" 
                className="text-gray-600 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 relative group"
              >
                {t('nav.home')}
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-600"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>

            {/* About Us */}
            <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
              <Link 
                to="/about" 
                className="text-gray-600 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 relative group"
              >
                {t('nav.about')}
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-600"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>

            {/* Company Formation Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <motion.button 
                className="text-gray-600 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1 relative group"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <span>{t('nav.companyFormation')}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-600"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: isServicesOpen ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                />
              </motion.button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 backdrop-blur-sm bg-white/95"
                  >
                    {isLoadingMenu ? (
                      <div className="px-5 py-4 text-center text-gray-500">
                        <div className="animate-pulse">Loading...</div>
                      </div>
                    ) : serviceMenuItems.length === 0 ? (
                      <div className="px-5 py-4 text-center text-gray-500">
                        {t('nav.noServices', 'No services available')}
                      </div>
                    ) : (
                      serviceMenuItems.map((service, index) => (
                      <motion.div
                        key={service.href}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Link
                          to={service.href}
                          className="group flex items-center px-5 py-4 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:text-primary-700 transition-all duration-200 border-b border-gray-100 last:border-b-0 relative overflow-hidden"
                        >
                          {/* Hover Effect Background */}
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-700 opacity-0 group-hover:opacity-5 transition-opacity duration-200" />

                          <div className="relative mr-4 rtl:ml-4 rtl:mr-0 w-8 h-6 flex items-center justify-center">
                            <img
                              src={service.flag}
                              alt={`${service.name} flag`}
                              className="w-full h-full object-cover rounded shadow-sm border border-gray-200 group-hover:border-primary-300 transition-colors duration-200"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentNode as HTMLElement;
                                if (parent && !parent.querySelector('.flag-fallback')) {
                                  const fallbackSpan = document.createElement('span');
                                  fallbackSpan.className = 'flag-fallback text-2xl group-hover:scale-110 transition-transform duration-200';
                                  fallbackSpan.textContent = service.fallback;
                                  parent.appendChild(fallbackSpan);
                                }
                              }}
                            />
                          </div>

                          <div className="flex-1 relative">
                            <span className="font-semibold block group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200">{service.name}</span>
                            <span className="text-xs text-gray-500 group-hover:text-primary-600 transition-colors duration-200">{t('common.formation', 'Company Formation')}</span>
                          </div>

                          <svg className="w-4 h-4 ml-auto rtl:mr-auto rtl:ml-0 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </motion.div>
                      ))
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Contact Us */}
            <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
              <Link 
                to="/contact" 
                className="text-gray-600 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 relative group"
              >
                {t('nav.contact')}
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-600"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>
          </motion.div>

          <div className="flex items-center space-x-4">
            {/* Language Toggle */}
            <motion.button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-600 font-medium transition-all duration-200 px-3 py-2 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 hidden md:block"
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
              title={i18n.language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
            >
              <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
            </motion.button>
            
            {/* Conditional Authentication UI */}
            {isAuthenticated ? (
              /* User Menu */
              <div 
                className="relative hidden md:block"
                onMouseEnter={() => setIsUserMenuOpen(true)}
                onMouseLeave={() => setIsUserMenuOpen(false)}
              >
                <motion.button
                  className="flex items-center space-x-3 rtl:space-x-reverse text-gray-700 hover:text-primary-600 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-transparent hover:border-primary-200 hover:bg-primary-50 relative"
                  whileHover={{ y: -1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="relative">
                    <div className="w-9 h-9 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-semibold text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold leading-tight">{user?.name}</span>
                  </div>
                  <svg className={`w-4 h-4 transition-transform duration-200 text-gray-400 ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </motion.button>

                {/* User Dropdown Menu */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 backdrop-blur-sm bg-white/95"
                    >
                      {/* User Menu Header */}
                      <div className="px-5 py-4 bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-200">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center shadow-md">
                            <span className="text-white font-semibold text-sm">
                              {user?.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{user?.name}</p>
                            <p className="text-xs text-primary-600">{t('auth.welcomeBack', 'Welcome back!')}</p>
                          </div>
                        </div>
                      </div>

                      <div className="py-2">
                        <Link
                          to="/dashboard"
                          className="group flex items-center px-5 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-200"
                        >
                          <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                          </svg>
                          <span className="group-hover:translate-x-1 transition-transform duration-200">{t('nav.dashboard')}</span>
                        </Link>

                        <Link
                          to="/dashboard/profile"
                          className="group flex items-center px-5 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-200"
                        >
                          <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="group-hover:translate-x-1 transition-transform duration-200">{t('dashboard.profile')}</span>
                        </Link>
                      </div>

                      <div className="border-t border-gray-100 py-2">
                        <button
                          onClick={handleLogout}
                          className="group w-full flex items-center px-5 py-3 text-sm text-red-600 hover:bg-red-50 transition-all duration-200"
                        >
                          <svg className="w-5 h-5 mr-3 text-red-400 group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span className="group-hover:translate-x-1 transition-transform duration-200">{t('dashboard.logout')}</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              /* Login and Register Buttons */
              <>
                {/* Login */}
                <motion.div
                  className="hidden md:block"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link 
                    to="/login" 
                    className="text-gray-600 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                  >
                    {t('nav.login')}
                  </Link>
                </motion.div>
                
                {/* Get Started Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link 
                    to="/register" 
                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">{t('nav.getStarted')}</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </Link>
                </motion.div>
              </>
            )}

            {/* Mobile menu button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-gray-100"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden border-t border-gray-200 bg-white"
            >
              <div className="px-4 py-4 space-y-4">
                <Link 
                  to="/" 
                  className="block text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.home')}
                </Link>
                <Link 
                  to="/about" 
                  className="block text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.about')}
                </Link>
                <div className="space-y-2">
                  <div className="text-gray-800 font-medium">{t('nav.companyFormation')}</div>
                  {isLoadingMenu ? (
                    <div className="pl-4 text-gray-500 text-sm animate-pulse">Loading...</div>
                  ) : serviceMenuItems.length === 0 ? (
                    <div className="pl-4 text-gray-500 text-sm">{t('nav.noServices', 'No services available')}</div>
                  ) : (
                    serviceMenuItems.map((service) => (
                      <Link
                        key={service.href}
                        to={service.href}
                        className="flex items-center pl-4 rtl:pr-4 rtl:pl-0 text-gray-600 hover:text-primary-600 transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <div className="mr-2 rtl:ml-2 rtl:mr-0 w-6 h-4 flex items-center justify-center">
                          <img
                            src={service.flag}
                            alt={`${service.name} flag`}
                            className="w-full h-full object-cover rounded-sm"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentNode as HTMLElement;
                              if (parent && !parent.querySelector('.flag-fallback')) {
                                const fallbackSpan = document.createElement('span');
                                fallbackSpan.className = 'flag-fallback text-lg';
                                fallbackSpan.textContent = service.fallback;
                                parent.appendChild(fallbackSpan);
                              }
                            }}
                          />
                        </div>
                        {service.name}
                      </Link>
                    ))
                  )}
                </div>
                <Link 
                  to="/contact" 
                  className="block text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.contact')}
                </Link>
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <button
                    onClick={toggleLanguage}
                    className="block w-full text-left text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                  >
                    {i18n.language === 'en' ? 'العربية' : 'English'}
                  </button>
                  
                  {/* Mobile Conditional Authentication UI */}
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse py-2">
                        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {user?.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-gray-700 font-medium">{user?.name}</span>
                      </div>
                      <Link 
                        to="/dashboard" 
                        className="block text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <Link 
                        to="/dashboard/profile" 
                        className="block text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('dashboard.profile')}
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full text-left text-red-600 hover:text-red-700 font-medium transition-colors duration-200"
                      >
                        {t('dashboard.logout')}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link 
                        to="/login" 
                        className="block text-gray-600 hover:text-primary-600 font-medium transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('nav.login')}
                      </Link>
                      <Link 
                        to="/register" 
                        className="block bg-primary-600 text-white px-4 py-2 rounded-lg font-medium text-center transition-colors duration-200 hover:bg-primary-700"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('nav.getStarted')}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;